import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import DataTable from 'components/shared/DataTable';
import Foldout from 'components/shared/Foldout';
import EditCostTiersButton from 'components/shared/highCostMeds/EditCostTiersButton';
import highCostMedListColumns from 'components/shared/highCostMeds/highCostMedListColumns';
import Search from 'components/shared/Search';
import { useProfile } from 'context/profile';
import { useDebounce } from 'hooks/useDebounce';
import { HighCostMedicationList } from 'models/HighCostMedicationList';
import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import { PERMISSIONS } from 'models/Profile';
import {
  adminHighCostMedicationListItemQueryKeys,
  indexAdminHighCostMedicationListItems,
  showAdminHighCostMedicationListItem,
} from 'services/api/admin/highCostMedicationListItems';
import { serializeSort } from 'stores/helpers';
import { colors } from 'styles/theme/colors';

import HighCostMedListEntryForm from './HighCostMedListEntryForm';
import HighCostMedRowMenu from './HighCostMedRowMenu';
import HighCostMedTierForm from './HighCostMedTierForm';
import RemoveHighCostMedModal from './RemoveHighCostMedModal';

// Date Added desc, matching the mock.
const DEFAULT_SORT: SortingState = [{ id: 'createdAt', desc: true }];

const PAGE_SIZE = 25;

// The in-panel views, each addressable. Remove is deliberately absent: it confirms in a centered
// modal over the panel, so the medication table stays visible behind it, and a half-finished
// deletion is not something to return to. See design 002, Admin UI.
type SlideoutViewType = 'list' | 'add' | 'edit' | 'tiers';

const PANEL_TITLES: Record<SlideoutViewType, string> = {
  list: 'High Cost Med List',
  add: 'New Medication',
  edit: 'Edit Medication',
  // No "Edit", unlike its add and edit siblings: the form's own section title already says Edit Cost
  // Tiers, and the header naming the thing rather than the action is what the design asks for.
  tiers: 'Cost Tiers',
};

// The medication id is nested under its resource name so two bare ids never sit side by side.
// `items` rather than `medications`: it mirrors the API resource, high_cost_medication_list_items.
const ITEMS_SEGMENT = 'items';
const EDIT_PATH = new RegExp(`^/${ITEMS_SEGMENT}/([^/]+)/edit$`);
const NEW_PATH = `/${ITEMS_SEGMENT}/new`;
// The cost tier key belongs to the list, not to any medication, so its segment sits directly under
// the list rather than under `items`.
const TIERS_PATH = '/tiers';

type Props = {
  highCostMedicationList: HighCostMedicationList;
  onClose: () => void;
};

// The medication table with search and the cost tier key tooltip, plus the add / edit / remove flows
// and the cost tier form. Adding, editing and describing tiers each replace the table with an
// in-panel form; removing confirms in a modal over it.
export default function HighCostMedListSlideout({ highCostMedicationList, onClose }: Props) {
  const [open, setOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<HighCostMedicationListItem | null>(null);
  const { pathname, search: locationSearch } = useLocation();
  const navigate = useNavigate();
  const { profile } = useProfile();

  const canCreateItem = profile.has(PERMISSIONS.adminHighCostMedicationListItemCreate);
  const canEditItem = profile.has(PERMISSIONS.adminHighCostMedicationListItemEdit);
  // The cost tier key is the list's own vocabulary rather than one of its medications, so it stays
  // on the list's edit permission while the medications ride their own. An admin can hold either
  // side without the other. See design 002, Authorization.
  const canEditTiers = profile.has(PERMISSIONS.adminHighCostMedicationListEdit);

  // The panel's own views are addressable, so which one is showing is read from the path rather
  // than held alongside it — one source of truth. Navigation between them replaces rather than
  // pushes, matching how the tab routes the panel itself (useAdminListPage.tsx), so the back button
  // leaves the tab rather than stepping back through the views.
  const basePath = `/admin/admissions/high-cost-medication-lists/${highCostMedicationList.id}`;
  const subPath = pathname.startsWith(basePath) ? pathname.slice(basePath.length) : '';
  const editItemId = subPath.match(EDIT_PATH)?.[1] ?? null;
  // A tier-less list opens the tier form too, on its empty state: the key is a rollup of what the
  // medications carry, so an empty key is not something this screen can fix — it has to say so
  // rather than be hidden behind a disabled control.
  const requestedView: SlideoutViewType =
    subPath === NEW_PATH ? 'add' : subPath === TIERS_PATH ? 'tiers' : editItemId ? 'edit' : 'list';

  // The write views are addressable, so hiding the controls that open them is not enough — an admin
  // handed an add, edit or tiers link would otherwise get a working form that 403s on save. Every
  // write view has to flow through here, not just the ones with a visible control, and each carries
  // its own permission: holding the medication grants is not a licence to rewrite the tier key.
  const viewPermitted: Record<SlideoutViewType, boolean> = {
    list: true,
    add: canCreateItem,
    edit: canEditItem,
    tiers: canEditTiers,
  };
  const view: SlideoutViewType = viewPermitted[requestedView] ? requestedView : 'list';

  // The query string is the tab's filters; entering and leaving a form must not drop them, the way
  // useAdminListPage.tsx carries it when routing the panel itself.
  const goToPanelPath = useCallback(
    (subPathname: string) =>
      navigate({ pathname: `${basePath}${subPathname}`, search: locationSearch }, { replace: true }),
    [navigate, basePath, locationSearch]
  );
  const goToList = useCallback(() => goToPanelPath(''), [goToPanelPath]);

  const [sort, setSort] = useState<SortingState>(DEFAULT_SORT);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  // An admin who landed on a write path they cannot use is shown the table; put the address bar
  // back in step so the URL is not describing a view that is not on screen.
  const requestedViewPermitted = viewPermitted[requestedView];
  useEffect(() => {
    if (!requestedViewPermitted) goToList();
  }, [requestedViewPermitted, goToList]);

  useEffect(() => {
    requestAnimationFrame(() => setOpen(true));
  }, []);

  // While a search is active, relevance-rank the results (sort_by=search) the way the denial list
  // slideout does. Changing the term re-keys the query, so there is no page index to reset.
  useEffect(() => {
    setSort(debouncedSearch ? [{ id: 'search', desc: true }] : DEFAULT_SORT);
  }, [debouncedSearch]);

  const animateClose = () => {
    setOpen(false);
    setTimeout(onClose, 300);
  };

  const queryParams = useMemo(
    () => ({ sortBy: serializeSort(sort), search: debouncedSearch }),
    [sort, debouncedSearch]
  );

  // DataTable scrolls rather than paginating, so pages accumulate as the body end is reached.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: adminHighCostMedicationListItemQueryKeys.index(highCostMedicationList.id, queryParams),
    queryFn: ({ signal, pageParam }) =>
      indexAdminHighCostMedicationListItems(
        highCostMedicationList.id,
        { ...queryParams, page: pageParam, pageSize: PAGE_SIZE },
        signal
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined,
  });

  const medications = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);
  const totalRecords = data?.pages[0]?.meta.totalRecords;

  // A medication linked directly is often not on a page the table has loaded, so it is fetched by
  // id; one already in hand skips the request.
  const loadedEditItem = medications.find((medication) => medication.id === editItemId);
  const { data: fetchedEditItem, isError: editItemFailed } = useQuery({
    queryKey: adminHighCostMedicationListItemQueryKeys.show(editItemId ?? ''),
    queryFn: ({ signal }) => showAdminHighCostMedicationListItem(editItemId!, signal),
    enabled: !!editItemId && !loadedEditItem && canEditItem,
  });
  const editItem = loadedEditItem ?? fetchedEditItem;

  // A link that no longer resolves drops back to the table rather than leaving the panel body
  // empty, the way a routed slideout drops an unresolvable id (useAdminListPage.tsx).
  useEffect(() => {
    if (editItemFailed) goToList();
  }, [editItemFailed, goToList]);

  const columns = useMemo(
    () =>
      highCostMedListColumns({
        tiers: highCostMedicationList.tiers,
        RowMenu: HighCostMedRowMenu,
        onEdit: (item) => goToPanelPath(`/${ITEMS_SEGMENT}/${item.id}/edit`),
        onRemove: (item) => setItemToRemove(item),
      }),
    [highCostMedicationList.tiers, goToPanelPath]
  );

  return (
    <>
      <Foldout open={open} position='right' onBackdropClick={animateClose} width='min(94%, 1120px)'>
        <PanelContainer>
          <PanelHeader>
            <TitleGroup>
              <PanelTitle>
                {highCostMedicationList.client?.name} &ndash; {PANEL_TITLES[view]}
              </PanelTitle>
              {/* The count describes the table, so it belongs to the view that shows it — a form
                has no rows to count, and a stale total under "New Medication" reads as the form's
                own. The em dash holds the line while the first page is in flight. */}
              {view === 'list' && <MedicationCount>{totalRecords ?? <>&mdash;</>} Medications Found</MedicationCount>}
            </TitleGroup>
            <CloseButton type='button' aria-label='Close' onClick={animateClose}>
              &times;
            </CloseButton>
          </PanelHeader>

          {view === 'list' && (
            <>
              {/* Search sits opposite the write actions, both above the table, per the design. */}
              <PanelToolbar>
                <SearchContainer>
                  <Search value={search} onChange={setSearch} placeholder='Search' />
                </SearchContainer>
                {/* Each write action carries its own gate, so an admin who curates the tier key but
                  does not maintain the medications sees only the link, and vice versa. A View Only
                  Olio Admin holds neither and sees an empty toolbar, and no kebab either. */}
                <ToolbarActions>
                  <AccessGuard accessor={PERMISSIONS.adminHighCostMedicationListEdit}>
                    <EditCostTiersButton onClick={() => goToPanelPath(TIERS_PATH)} />
                  </AccessGuard>
                  <AccessGuard accessor={PERMISSIONS.adminHighCostMedicationListItemCreate}>
                    <Button variant='primary' onClick={() => goToPanelPath(NEW_PATH)}>
                      Add New Medication
                    </Button>
                  </AccessGuard>
                </ToolbarActions>
              </PanelToolbar>

              <PanelContent>
                <DataTable
                  columns={columns}
                  data={medications}
                  loading={isLoading}
                  // Sorting and searching are resolved by the API (trigram relevance among them), so
                  // the table must not re-sort the rows it was handed.
                  manualSorting
                  sorting={sort}
                  onSortingChange={setSort}
                  hasMore={hasNextPage}
                  onBodyEndReached={() => {
                    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
                  }}
                  hasFooter={false}
                  maxHeight='calc(100vh - 232px)'
                />
              </PanelContent>
            </>
          )}

          {/* On a cold edit link the medication is still in flight; the form waits for it rather
            than rendering blank and then filling in. */}
          {(view === 'add' || (view === 'edit' && editItem)) && (
            <PanelContent>
              <HighCostMedListEntryForm
                highCostMedicationList={highCostMedicationList}
                highCostMedicationListItem={editItem}
                onClose={goToList}
                // A medication added while a search is active would otherwise land behind that
                // filter, leaving a filtered-empty table under an "added successfully" toast.
                onSaved={() => {
                  if (view === 'add') setSearch('');
                }}
              />
            </PanelContent>
          )}

          {view === 'tiers' && (
            <PanelContent>
              <HighCostMedTierForm highCostMedicationList={highCostMedicationList} onClose={goToList} />
            </PanelContent>
          )}
        </PanelContainer>
      </Foldout>

      {/* Rendered outside the Foldout: the panel's transform would otherwise make it the containing
        block for the modal's fixed backdrop, centering the confirm inside the panel and leaving the
        Foldout's own backdrop live underneath it. Mirrors AdmissionSlideout.tsx. */}
      {itemToRemove && (
        <ModalWrapper>
          <RemoveHighCostMedModal highCostMedicationListItem={itemToRemove} onClose={() => setItemToRemove(null)} />
        </ModalWrapper>
      )}
    </>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 32px 20px;
  border-bottom: 1px solid ${colors.black10};
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PanelTitle = styled.h2`
  font-size: ${({ theme }) => theme.textStyles.h3.fontSize};
  line-height: ${({ theme }) => theme.textStyles.h3.lineHeight};
  font-weight: ${({ theme }) => theme.textStyles.h3.fontWeight};
  margin: 0;
`;

const MedicationCount = styled.span`
  font-size: ${({ theme }) => theme.textStyles.label.fontSize};
  line-height: ${({ theme }) => theme.textStyles.label.lineHeight};
  color: ${colors.black50};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const PanelToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 24px 32px;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const SearchContainer = styled.div`
  width: 320px;
`;

const PanelContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 32px 24px;
`;

// Lifts the confirm above the Foldout's panel (z-index 900) now that it no longer sits inside it.
const ModalWrapper = styled.div`
  position: relative;
  z-index: 1200;
`;
